import appStore from '../mobx/appStore';
import { signOutFirebase } from './firebaseAuth';
import {
  buildOidcLogoutUrl,
  canReturnToApp,
  clearIdpIdToken,
  clearAppCookies,
} from './idpLogout';
import { markAutoSigninAttempted } from './autoSignin';

/**
 * Sign out of BOTH the Firebase Auth session AND the id.utah.gov (ForgeRock AM)
 * SSO session.
 *
 * The app-side sign-out is done LOCALLY and UP-FRONT so it always happens: it
 * never depends on the browser returning from the IdP (a full-page IdP redirect
 * that cannot come back would otherwise leave the app still signed in). Order:
 *
 *   1. Block the sign-out control's default action (so the page can't refresh
 *      mid-logout), suppress the landing-page auto sign-in, build AM's endSession
 *      logout URL (capturing the `id_token_hint`), then DELETE local data (the
 *      id_token hint) and cookies.
 *   2. Sign out of Firebase and clear the in-memory session — guaranteed.
 *   3. End the id.utah.gov SSO session: redirect the browser to AM's
 *      `endSession?id_token_hint=…&post_logout_redirect_uri=…` ONLY when a
 *      REGISTERED `post_logout_redirect_uri` is configured (so AM returns to the
 *      app). Otherwise — to avoid stranding the user on AM's logged-out page —
 *      land locally on the app's own signed-out page. (Full SSO logout with
 *      redirect-back requires DTS to register the return URL on the AM client
 *      and `FIREBASE_OIDC_POST_LOGOUT_REDIRECT_URI` to be set to it.)
 */

// Firebase sign-out is normally a fast local operation, but it must never STALL
// the redirect: if signOut(auth) hangs, the finalize proceeds either when it
// settles OR after this timeout — whichever comes first.
const FIREBASE_SIGNOUT_TIMEOUT_MS = 4000;

// Resolve when `promise` settles (success or failure) OR after `ms`, whichever
// is first. The pending timer is cleared once the promise settles so it never
// dangles (which would otherwise leak a timer / keep the test runner alive).
const settleOrTimeout = (promise, ms) =>
  new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (!settled) {
        settled = true;
        resolve();
      }
    };
    const timer = setTimeout(finish, ms);
    Promise.resolve(promise)
      .catch(() => undefined)
      .finally(() => {
        clearTimeout(timer);
        finish();
      });
  });

export const signOutEverywhere = (event) => {
  // Block the sign-out control's default action (e.g. following its <a> link) so
  // the page does NOT refresh/navigate before the teardown + redirect below.
  if (event && typeof event.preventDefault === 'function') {
    event.preventDefault();
  }

  // Suppress the landing-page auto sign-in so the signed-out landing does not
  // immediately fire a new login redirect.
  markAutoSigninAttempted();

  // Build AM's endSession URL (reads the id_token_hint) BEFORE purging local
  // data, then delete the hint + cookies.
  const logoutUrl = buildOidcLogoutUrl();
  clearIdpIdToken();
  clearAppCookies();

  // Guaranteed app-side sign-out, then redirect. Only do the full-page IdP
  // logout when AM can return to the app; otherwise land locally (no stranding).
  return settleOrTimeout(signOutFirebase(), FIREBASE_SIGNOUT_TIMEOUT_MS).then(
    () => {
      appStore.signOut();
      const origin =
        typeof window !== 'undefined' && window.location
          ? window.location.origin
          : '';
      const target = canReturnToApp() && logoutUrl ? logoutUrl : origin;
      redirect.to(target);
    },
  );
};

export const redirect = {
  to: (target) => {
    if (
      typeof window !== 'undefined' &&
      window.location &&
      typeof window.location.assign === 'function' &&
      target
    ) {
      window.location.assign(target);
    }
  },
};

export default signOutEverywhere;
